import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9oo7o0senbniolesing';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: t('seo.aboutTitle'),
    description: t('seo.aboutDesc'),
    url: 'https://www.qimiaomanhua.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (t('about.faqs', { returnObjects: true }) as Array<{ q: string; a: string }>)?.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })) || [],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formData.delete('website_alt');

    setFormStatus('submitting');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const resp = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await resp.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = (parsed as { meta?: { message?: string; detail?: string } })?.meta?.message
        || (parsed as { message?: string })?.message
        || '';

      if (resp.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const errMsg = (parsed as { meta?: { detail?: string } })?.meta?.detail
          || serverMsg
          || responseText
          || t('about.formError');
        setFormError(errMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError(t('about.formError'));
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title={t('seo.aboutTitle')}
        description={t('seo.aboutDesc')}
        canonicalUrl="https://www.qimiaomanhua.com/about"
        schema={pageSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.about') },
              ]}
            />
          </div>

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}></script>

          {/* Page Title */}
          <div className="py-8 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">{t('about.title')}</h1>
            <p className="text-foreground-500 mt-3 max-w-2xl mx-auto">{t('about.brandDesc1')}</p>
          </div>

          {/* Brand Story */}
          <section className="py-8" aria-labelledby="brand-heading">
            <h2 id="brand-heading" className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('about.brandTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <p className="text-foreground-600 leading-relaxed text-sm">{t('about.brandDesc1')}</p>
                <p className="text-foreground-600 leading-relaxed text-sm">{t('about.brandDesc2')}</p>
                <p className="text-foreground-600 leading-relaxed text-sm">{t('about.brandDesc3')}</p>
              </div>
              <div className="rounded-lg overflow-hidden aspect-video">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cozy%20creative%20office%20space%20with%20comic%20books%20and%20manga%20on%20bookshelves%2C%20young%20Asian%20team%20collaborating%20around%20wooden%20table%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20casual%20creative%20workspace%20vibe%2C%20soft%20beige%20and%20warm%20wood%20tones%2C%20clean%20editorial%20photography%20style&width=800&height=450&seq=about-brand-2026&orientation=landscape"
                  alt="奇妙漫画团队办公环境"
                  title="奇妙漫画 | 团队办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-10" aria-labelledby="features-heading">
            <h2 id="features-heading" className="font-heading text-2xl font-bold text-foreground-900 text-center mb-8">{t('about.featureTitle')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(t('about.features', { returnObjects: true }) as Array<{ title: string; desc: string; icon: string }>)?.map((feature, idx) => (
                <div key={idx} className="p-6 bg-background-50 rounded-lg border border-background-200 hover:border-primary-200 transition-all group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all mb-4">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="py-10" aria-labelledby="stats-heading">
            <h2 id="stats-heading" className="font-heading text-2xl font-bold text-foreground-900 text-center mb-8">{t('about.statsTitle')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {(t('about.stats', { returnObjects: true }) as Array<{ label: string; value: string }>)?.map((stat, idx) => (
                <div key={idx} className="text-center p-6 bg-primary-50 rounded-lg">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10" aria-labelledby="testimonials-heading">
            <h2 id="testimonials-heading" className="font-heading text-2xl font-bold text-foreground-900 text-center mb-8">{t('about.testimonialTitle')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {(t('about.testimonials', { returnObjects: true }) as Array<{ name: string; role: string; content: string }>)?.map((testimonial, idx) => (
                <div key={idx} className="p-6 bg-background-50 rounded-lg border border-background-200">
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{testimonial.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-bold text-sm">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{testimonial.name}</div>
                      <div className="text-xs text-foreground-400">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact + FAQ */}
          <section className="py-10 border-t border-background-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">{t('about.contactTitle')}</h2>
                <p className="text-sm text-foreground-500 mb-6">{t('about.contactDesc')}</p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-foreground-400 text-xs">{t('about.email')}</div>
                      <div className="text-foreground-800 font-medium">{t('about.emailValue')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <div className="text-foreground-400 text-xs">{t('about.wechat')}</div>
                      <div className="text-foreground-800 font-medium">{t('about.wechatValue')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-weibo-line"></i>
                    </div>
                    <div>
                      <div className="text-foreground-400 text-xs">{t('about.weibo')}</div>
                      <div className="text-foreground-800 font-medium">{t('about.weiboValue')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                      <i className="ri-qq-line"></i>
                    </div>
                    <div>
                      <div className="text-foreground-400 text-xs">{t('about.qq')}</div>
                      <div className="text-foreground-800 font-medium">{t('about.qqValue')}</div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">{t('about.contactForm')}</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-hp-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1">{t('about.formName')}</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1">{t('about.formEmail')}</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1">{t('about.formMessage')}</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '提交中...' : t('about.formSubmit')}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">{t('about.formSuccess')}</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>

              {/* FAQ */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('about.faqTitle')}</h2>
                <div className="space-y-4">
                  {(t('about.faqs', { returnObjects: true }) as Array<{ q: string; a: string }>)?.map((faq, idx) => (
                    <details key={idx} className="group bg-background-50 rounded-lg border border-background-200">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}